/**
 * One row of Instanssit (InstanceManager.jsx): status, name with its labels
 * and badges, participants, workshop date, and the row's actions. Archived
 * rows (archived_at, #1209) offer Palauta and the red Poista; current rows
 * Avaa and Arkistoi. "Tunnisteet" opens the label picker inline.
 */
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Lock, Wifi, ArrowRight, Archive, ArchiveRestore, Trash2, Tag } from 'lucide-react';
import { STATUS_VALUES, getStatusLabel } from '../utils/statusTranslations';
import { useTranslation } from '../hooks/useTranslation.jsx';
import LabelChips from './InstanceLabels/LabelChips';
import LabelPicker from './InstanceLabels/LabelPicker';

export const STATUS_COLORS = {
  [STATUS_VALUES.DRAFT]: '#9ca3af',
  [STATUS_VALUES.PRE_WORKSHOP]: '#60a5fa',
  [STATUS_VALUES.RUNNING]: '#4ade80',
  [STATUS_VALUES.POST_WORKSHOP]: '#c084fc',
  [STATUS_VALUES.ARCHIVED]: '#6b7280',
};

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid ${p => (p.$danger ? 'rgba(244, 67, 54, 0.5)' : '#333')};
  background: ${p => (p.$danger ? 'rgba(244, 67, 54, 0.15)' : 'rgba(255, 255, 255, 0.05)')};
  color: ${p => (p.$danger ? '#ff8a80' : '#fff')};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) { filter: brightness(1.15); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  svg { width: 14px; height: 14px; }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr) 90px 110px auto;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  cursor: pointer;
  opacity: ${p => (p.$archived ? 0.7 : 1)};

  &:hover { background: rgba(255, 255, 255, 0.03); }

  @media (max-width: 760px) {
    grid-template-columns: minmax(0, 1fr) auto;
    .hide-narrow { display: none; }
  }
`;

const Status = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${p => p.$color};

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${p => p.$color};
  }
`;

const Name = styled.div`
  min-width: 0;

  strong { display: block; font-size: 15px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  small { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin-top: 4px; font-size: 12px; color: rgba(255, 255, 255, 0.5); }
  small svg { width: 12px; height: 12px; vertical-align: -1px; margin-right: 3px; }
`;

const Meta = styled.span`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
`;

const Actions = styled.div`
  display: flex;
  gap: 6px;
`;

const Editor = styled.div`
  padding: 4px 16px 16px 152px;
  border-top: 1px dashed rgba(255, 255, 255, 0.07);
  background: rgba(255, 255, 255, 0.02);

  @media (max-width: 760px) { padding-left: 16px; }
`;

const EditorActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
`;

export default function InstanceManagerRow({ instance: i, onOpen, onSaveTags, onArchive, onRestore, onDelete }) {
  const { t, language } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(i.tags || []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const archived = !!i.archived_at;

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const saveTags = () => run(async () => {
    await onSaveTags(i, draft);
    setEditing(false);
  });

  // metadata.workshop_date is free text ("7.–8.9.2026"), shown as written.
  const date = i.metadata?.workshop_date || '—';

  return (
    <>
      <Row role="button" tabIndex={0} $archived={archived}
        onClick={() => onOpen(i)}
        onKeyDown={(e) => { if (e.key === 'Enter') onOpen(i); }}
      >
        <Status className="hide-narrow" $color={STATUS_COLORS[i.status] || '#9ca3af'}>
          {getStatusLabel(i.status, language)}
        </Status>
        <Name>
          <strong>{i.title}</strong>
          <small>
            <LabelChips tags={i.tags} emptyText={t('instanceLabels.none')} />
            <span>{(i.language || 'fi').toUpperCase()}</span>
            <span>{i.instance_type === 'online' ? t('instanceSettingsModal.typeOnline') : t('instanceSettingsModal.typeLive')}</span>
            {i.settings?.locked && <span><Lock />{t('adminRunStrip.locked')}</span>}
            {i.instance_type !== 'online' && i.settings?.remoteEntry && <span><Wifi />{t('settingsPages.remoteEntryLabel')}</span>}
            {archived && <span><Archive />{t('instanceList.archivedOn', { date: new Date(i.archived_at).toLocaleDateString('fi-FI') })}</span>}
          </small>
        </Name>
        <Meta className="hide-narrow" title={t('instanceList.participants')}>
          {t('instanceList.participantCount', { count: i.assigned_pins || 0 })}
        </Meta>
        <Meta className="hide-narrow" title={t('instanceList.workshopDate')}>{date}</Meta>
        <Actions onClick={(e) => e.stopPropagation()}>
          <Button type="button" title={t('instanceLabels.edit')} aria-label={t('instanceLabels.edit')}
            onClick={() => { setDraft(i.tags || []); setEditing((v) => !v); }} disabled={busy}>
            <Tag />
          </Button>
          {archived ? (
            <>
              <Button type="button" onClick={() => run(() => onRestore(i))} disabled={busy}>
                <ArchiveRestore /> {t('instanceList.restore')}
              </Button>
              <Button type="button" $danger onClick={() => onDelete(i)} disabled={busy}>
                <Trash2 /> {t('common.delete')}
              </Button>
            </>
          ) : (
            <>
              <Button type="button" title={t('instanceList.archiveHint')} onClick={() => run(() => onArchive(i))} disabled={busy}>
                <Archive /> {t('instanceList.archive')}
              </Button>
              <Button type="button" onClick={() => onOpen(i)}>
                {t('instanceList.open')} <ArrowRight />
              </Button>
            </>
          )}
        </Actions>
      </Row>
      {(editing || error) && (
        <Editor>
          {editing && (
            <>
              <LabelPicker value={draft} onChange={setDraft} disabled={busy} />
              <EditorActions>
                <Button type="button" onClick={saveTags} disabled={busy || draft.length === 0}>{t('common.save')}</Button>
                <Button type="button" onClick={() => setEditing(false)} disabled={busy}>{t('common.cancel')}</Button>
              </EditorActions>
            </>
          )}
          {error && <div style={{ marginTop: 8, fontSize: 13, color: '#f87171' }}>{t('settingsPages.saveFailed', { error })}</div>}
        </Editor>
      )}
    </>
  );
}
